import React, { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { clsx } from 'clsx';
import { X } from 'lucide-react';
import styles from './Modal.module.css';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode;
  /** Extra header controls, left of the close button. Use `ModalIconButton` so they match it. */
  headerActions?: React.ReactNode;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  compact?: boolean;
  // 'side' renders a full-height panel anchored to the right instead of a
  // centered dialog — used for record-detail views.
  variant?: 'center' | 'side';
  /** Ignore Escape and overlay clicks (e.g. while a confirm action is in flight). */
  disableDismiss?: boolean;
}

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function Modal({
  isOpen,
  onClose,
  title,
  icon,
  children,
  footer,
  headerActions,
  size = 'md',
  compact,
  variant = 'center',
  disableDismiss = false,
}: ModalProps) {
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement>(null);

  // Move focus into the dialog on open and give it back on close, so keyboard
  // users don't keep operating the page behind the overlay.
  useEffect(() => {
    if (!isOpen) return;
    const previous = document.activeElement as HTMLElement | null;
    const panel = panelRef.current;
    const first = panel?.querySelector<HTMLElement>(FOCUSABLE);
    (first ?? panel)?.focus();
    return () => previous?.focus?.();
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !disableDismiss) onClose();
      if (e.key === 'Tab' && panelRef.current) {
        // Keep Tab inside the dialog.
        const items = Array.from(panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE));
        if (items.length === 0) return;
        const firstEl = items[0];
        const lastEl = items[items.length - 1];
        if (e.shiftKey && document.activeElement === firstEl) {
          e.preventDefault();
          lastEl.focus();
        } else if (!e.shiftKey && document.activeElement === lastEl) {
          e.preventDefault();
          firstEl.focus();
        }
      }
    };
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose, disableDismiss]);

  if (!isOpen) return null;

  // Rendered at the end of the page, so no sticky or layered parent can put other controls above the backdrop.
  return createPortal(
    <div
      className={clsx(styles.overlay, variant === 'side' && styles.sideOverlay)}
      onClick={disableDismiss ? undefined : onClose}
      role="dialog"
      aria-modal
      aria-labelledby={title ? titleId : undefined}
    >
      <div
        ref={panelRef}
        tabIndex={-1}
        className={clsx(
          styles.modal,
          styles[size],
          compact && styles.compact,
          variant === 'side' && styles.sidePanel,
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {title && (
          <div className={styles.header}>
            <div className={styles.titleWrap}>
              {icon && (
                <span className={styles.titleIcon} aria-hidden="true">
                  {icon}
                </span>
              )}
              <h2 id={titleId} className={styles.title}>
                {title}
              </h2>
            </div>
            <div className={styles.headerActions}>
              {headerActions}
              <ModalIconButton label="Close" onClick={onClose}>
                <X size={16} aria-hidden />
              </ModalIconButton>
            </div>
          </div>
        )}
        <div className={styles.body}>{children}</div>
        {footer && <div className={styles.footer}>{footer}</div>}
      </div>
    </div>,
    document.body,
  );
}

/** A header icon button styled like the modal's close button. */
export function ModalIconButton({
  label,
  onClick,
  children,
}: {
  label: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      className={styles.closeButton}
      onClick={onClick}
      aria-label={label}
      title={label}
    >
      {children}
    </button>
  );
}
